import { getCurrentInstance, inject, type Component, type InjectionKey } from 'vue'
import type { CRUDGateway } from '@cplug/frontend-core/gateways'
import type { Result } from '@cplug/frontend-core/utils'
import type { DashboardUser } from '../users/models/DashboardUser'
import type { UserPermissionsGroup } from '../permission-groups/models/UserPermissionsGroup'
import type { UserSession } from '../user-sessions/models/UserSession'
import type { UserPermission } from '../permissions/models/UserPermision'
import { CPUserGateway } from '../users/gateways/CPUserGateway'
import { CPUserPermissionsGroupGateway } from '../permission-groups/gateways/CPUserPermissionsGroupGateway'
import { CPUserSessionGateway } from '../user-sessions/gateways/CPUserSessionGateway'

export interface UserModuleFeatures {
  showExcludedUsersTab: boolean
  showUserLimitBanner: boolean
  showProfileListFilter: boolean
  showAccessProfileColumn: boolean
  showUserSettingsSection: boolean
  showCompaniesSelection: boolean
  showSyncNewCompaniesToggle: boolean
  emailReadonlyOnEdit: boolean
  canRestoreUsers: boolean
  skipSalesPersonValidation: boolean
  userNameMinLength: number
  userNameMaxLength: number
  profileNameMinLength: number
  profileNameMaxLength: number
  requireUserPasswordOnCreate: boolean
  validateUserEmailOnCreateOnly: boolean
  loadProfilePermissionsSeparately: boolean
}

export const DEFAULT_USER_MODULE_FEATURES: UserModuleFeatures = {
  showExcludedUsersTab: true,
  showUserLimitBanner: true,
  showProfileListFilter: true,
  showAccessProfileColumn: true,
  showUserSettingsSection: true,
  showCompaniesSelection: true,
  showSyncNewCompaniesToggle: false,
  emailReadonlyOnEdit: false,
  canRestoreUsers: true,
  skipSalesPersonValidation: false,
  userNameMinLength: 1,
  userNameMaxLength: 120,
  profileNameMinLength: 1,
  profileNameMaxLength: 999,
  requireUserPasswordOnCreate: true,
  validateUserEmailOnCreateOnly: false,
  loadProfilePermissionsSeparately: true
}

export const USER_MODULE_FEATURES_KEY: InjectionKey<Partial<UserModuleFeatures>> = Symbol('userModuleFeatures')

export function useUserModuleFeatures (): UserModuleFeatures {
  const injectedOverrides = inject(USER_MODULE_FEATURES_KEY, {})

  return {
    ...DEFAULT_USER_MODULE_FEATURES,
    ...userModuleFeaturesOverride,
    ...injectedOverrides
  }
}

export type UserSessionGateway = CRUDGateway<UserSession> & {
  revokeSession: (sessionId: string) => Promise<Result<void>>
}

export interface UserProfilePermissionCatalogGateway {
  loadCatalog: () => Promise<Result<UserPermission[]>>
  resolveSelectedPermissions: (profile: UserPermissionsGroup, catalog: UserPermission[]) => UserPermission[]
}

let userGatewayOverride: CRUDGateway<DashboardUser> | null = null
let profileGatewayOverride: CRUDGateway<UserPermissionsGroup> | null = null
let userSessionGatewayOverride: UserSessionGateway | null = null
let userModuleFeaturesOverride: Partial<UserModuleFeatures> | null = null

export function registerUserModuleFeatures (features: Partial<UserModuleFeatures>): void {
  userModuleFeaturesOverride = features
}

export function registerUserGateway (gateway: CRUDGateway<DashboardUser>): void {
  userGatewayOverride = gateway
}

export function registerProfileGateway (gateway: CRUDGateway<UserPermissionsGroup>): void {
  profileGatewayOverride = gateway
}

export function registerUserSessionGateway (gateway: UserSessionGateway): void {
  userSessionGatewayOverride = gateway
}

export const USER_GATEWAY_KEY: InjectionKey<CRUDGateway<DashboardUser>> = Symbol('userGateway')
export const PROFILE_GATEWAY_KEY: InjectionKey<CRUDGateway<UserPermissionsGroup>> = Symbol('profileGateway')
export const USER_SESSION_GATEWAY_KEY: InjectionKey<UserSessionGateway> = Symbol('userSessionGateway')
export const PROFILE_PERMISSIONS_FORM_KEY: InjectionKey<Component> = Symbol('profilePermissionsForm')

function resolveInjectedGateway<T> (key: InjectionKey<T>, fallback: T): T {
  if (getCurrentInstance()) {
    return inject(key, null) ?? fallback
  }

  return fallback
}

export function useUserGateway (): CRUDGateway<DashboardUser> {
  return userGatewayOverride ?? resolveInjectedGateway(USER_GATEWAY_KEY, CPUserGateway.instance())
}

export function useProfileGateway (): CRUDGateway<UserPermissionsGroup> {
  return profileGatewayOverride ?? resolveInjectedGateway(PROFILE_GATEWAY_KEY, CPUserPermissionsGroupGateway.instance())
}

export function useUserSessionGateway (): UserSessionGateway {
  return userSessionGatewayOverride ?? resolveInjectedGateway(USER_SESSION_GATEWAY_KEY, CPUserSessionGateway.instance())
}

export function useProfilePermissionsForm (): Component | null {
  return inject(PROFILE_PERMISSIONS_FORM_KEY, null)
}
